import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Button } from "../components/Button";
import { Chip, ChipRow } from "../components/Chip";
import { DeclareKmSheet } from "../components/DeclareKmSheet";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { dayLabel, fmtClock, todayStr } from "../domain/date";
import { noticedLine, noticedOfferFor } from "../domain/noticedWalks";
import { sessionKeyFor } from "../domain/plan";
import { heatWindow } from "../domain/programme";
import { SESSION_LABEL } from "../domain/session";
import { easyTreadmillLine, sessionGate } from "../domain/trainingQuestions";
import type { WalkLog } from "../domain/types";
import { cadenceLine, walkKcal, walkMeasuredSteps, walkSecs } from "../domain/walk";
import { INTERVAL_ROUNDS, INTERVAL_TOTAL_SECS } from "../domain/walkIntervals";
import { clockTime } from "../domain/week";
import { beep } from "../lib/device";
import { useDeviceAnswers, useKaya, usePlayerMode } from "../store/kayaStore";
import { addNoticedWalk } from "../store/toggles";
import "./Train.css";

/** The planned lengths on offer, minutes; null is an open walk. */
const PLAN_CHOICES: (number | null)[] = [20, 30, 45, 60, null];
const INTERVAL_LABEL = `Interval walk · ${INTERVAL_TOTAL_SECS / 60} min`;

const planWords = (m: number | null | undefined) => (typeof m === "number" ? `${m} min` : "Open");

/** "2,625 steps · 105 a minute · brisk · ~96 kcal"; a noticed walk never carries the cadence word —
 *  its window is two fifteen-minute samples, so the figure would mislead (ADR-063). */
function walkSubtitle(w: WalkLog) {
  const measured = typeof w.measuredSteps === "number";
  // A measured zero is a measurement — the phone was not on you — not "pending".
  const steps = measured ? `${(w.measuredSteps ?? 0).toLocaleString()} steps` : "steps pending";
  const cadence = w.noticed ? null : cadenceLine(w);
  const kcal = `~${w.kcal} kcal${measured ? "" : " est."}`;
  const rounds = w.mode === "intervals" ? `${w.rounds ?? 0} of ${INTERVAL_ROUNDS} brisk rounds · ` : "";
  return (
    <>
      {rounds}
      {[steps, cadence, kcal].filter(Boolean).join(" · ")}
      {w.noticed ? (
        <>
          {" "}
          <Tag tone="neutral">noticed</Tag>
        </>
      ) : null}
    </>
  );
}

/**
 * Free walks — outdoors, indoors, an errand — each recorded as its own session rather than
 * disappearing into the day's total. Kaya times it; the phone fills in the steps afterwards.
 * This is the whole of the cardio page for someone with no treadmill (ADR-022).
 *
 * Before the Start: how long the walk is meant to be (for the "Half-way. Turn back now." cue) and
 * whether cues are spoken — the player's one voice switch. The interval walk is offered beside
 * Start only where the day's safety answers allow fast work. A walk the counter noticed is offered
 * above the day's walks and saved only on a tap. (ADR-063)
 */
export default function TrainWalks() {
  const S = useKaya((s) => s.S);
  const session = useKaya((s) => s.session);
  const meta = useKaya((s) => s.meta);
  const update = useKaya((s) => s.update);
  const setPlayerPrefs = useKaya((s) => s.setPlayerPrefs);
  const setWalkMeta = useKaya((s) => s.setWalkMeta);
  const player = usePlayerMode();
  const device = useDeviceAnswers();
  const history = useHistory();
  /** index of the walk whose distance is being declared, while the sheet is up */
  const [declaring, setDeclaring] = useState<number | null>(null);
  const [choosingLength, setChoosingLength] = useState(false);

  const today = todayStr();
  const walks = S.logs[today]?.walks ?? [];
  const measuredAny = walks.some((w) => typeof w.measuredSteps === "number");
  const pending = walks.some((w) => typeof w.measuredSteps !== "number");
  const declared = walks.some((w) => typeof w.kmDeclared === "number");
  /** What a Start button says while a session is open: what it will actually do. */
  const resumeLabel = session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : null;

  const open = session && session.kind !== "workout" ? { startedAt: session.startedAt, secs: (Date.now() - Date.parse(session.startedAt)) / 1000 } : null;
  const offer = noticedOfferFor(S, today, meta.noticedOff, open);

  // Fast work is what the interval walk is: the treadmill's brisk phases and B's intervals go off
  // for the same answers, so the same gate holds it.
  const gate = sessionGate(S, sessionKeyFor(S, today), device);
  const intervalsOk = !gate.stop && !gate.easyTreadmill;
  const intervalsWhy = gate.stop ? gate.line : easyTreadmillLine(S, device);
  const now = new Date();
  const heat = heatWindow(today, now.getHours() + now.getMinutes() / 60);

  // One session at a time: with one open, Start goes back to it. (ADR-027)
  const startWalk = (route: string) => {
    if (session) {
      history.push(session.route, { from: history.location.pathname });
      return;
    }
    // beeping here, inside the click, is also what unlocks the AudioContext for the player
    beep(880, 120, 0.15, S.profile.sound);
    history.push(route, { from: history.location.pathname });
  };

  const kcal = Math.round(walkKcal(walks));

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Walks" back={{ to: "/train", label: "Back to Train" }} />

            {walks.length ? (
              <Hero
                label={`Walks · ${dayLabel(today)}`}
                figure={fmtClock(walkSecs(walks) * 1000)}
                unit={`${walks.length} ${walks.length === 1 ? "walk" : "walks"} today`}
                line={
                  measuredAny ? (
                    <>
                      {walkMeasuredSteps(walks).toLocaleString()} steps measured <Tag tone="phone" />
                      {pending ? " · more pending" : ""}
                    </>
                  ) : (
                    "Steps pending — the phone fills them in"
                  )
                }
                sub={`~${kcal.toLocaleString()} kcal${measuredAny && !pending ? "" : " est."}${declared ? "" : " · distance not declared yet"}`}
              />
            ) : (
              <Hero
                label={`Walks · ${dayLabel(today)}`}
                figure={null}
                line="Time a walk here; your phone fills in the steps."
              />
            )}

            {walks.length || offer ? (
              <RowGroup heading="Today">
                {offer ? (
                  <NavRow
                    kind="action"
                    icon={I.footsteps}
                    tone="sky"
                    title={`${noticedLine(offer, clockTime)} · noticed`}
                    subtitle="Your phone counted a walk that was not timed"
                    action={
                      <Button
                        variant="ghost"
                        onClick={() => update((d) => addNoticedWalk(d, today, offer, Date.now()))}
                        ariaLabel={`Save the ${clockTime(offer.startedAt)} walk`}
                      >
                        Save
                      </Button>
                    }
                  />
                ) : null}
                {walks.map((w, i) => (
                  <NavRow
                    key={`${w.startedAt}:${i}`}
                    kind="action"
                    icon={I.walk}
                    tone="sky"
                    title={`${clockTime(w.startedAt)} · ${fmtClock(w.secs * 1000)}${w.mode === "intervals" ? " · intervals" : ""}`}
                    subtitle={walkSubtitle(w)}
                    action={
                      <Button
                        variant="ghost"
                        onClick={() => setDeclaring(i)}
                        ariaLabel={
                          typeof w.kmDeclared === "number"
                            ? `${w.kmDeclared.toFixed(2)} km for the ${clockTime(w.startedAt)} walk, change it`
                            : `Add km to the ${clockTime(w.startedAt)} walk`
                        }
                      >
                        {typeof w.kmDeclared === "number" ? `${w.kmDeclared.toFixed(2)} km` : "Add km"}
                      </Button>
                    }
                  />
                ))}
              </RowGroup>
            ) : null}

            {walks.length ? (
              <p className="train-fine">
                Declare distance from a map, a track or a watch and Kaya calibrates your stride.
              </p>
            ) : null}

            <RowGroup heading="Before you start">
              <NavRow
                icon={I.clock}
                title="Length"
                subtitle={meta.walkPlanMin ? "Half-way, you hear “turn back now”" : "Walk as long as you like"}
                value={planWords(meta.walkPlanMin)}
                onClick={() => setChoosingLength(true)}
              />
              <NavRow
                kind="toggle"
                icon={I.bell}
                title="Spoken cues"
                subtitle="Every 5 minutes: the time and your steps"
                checked={player.voice}
                onChange={(on) => setPlayerPrefs({ voice: on })}
              />
              {intervalsOk ? null : (
                <NavRow kind="disabled" icon={I.walk} title={INTERVAL_LABEL} subtitle={intervalsWhy ?? "Not today"} />
              )}
            </RowGroup>

            <p className="train-fine">
              The interval walk: {INTERVAL_ROUNDS} × 3 min brisk + 3 min easy, after an easy three; brisk means
              breathing harder, still able to talk.
              {heat ? ` ${heat.line}.` : ""}
            </p>

            <ActionBar
              primary={{ label: resumeLabel ?? "Start a walk", onClick: () => startWalk("/walk"), icon: I.walk }}
              secondary={
                intervalsOk && !session ? { label: INTERVAL_LABEL, onClick: () => startWalk("/walk?mode=intervals") } : undefined
              }
            />
          </div>
        </Screen>
        {declaring !== null ? (
          <DeclareKmSheet ds={today} index={declaring} onClose={() => setDeclaring(null)} />
        ) : null}
        {choosingLength ? (
          <Sheet
            title="How long a walk?"
            sub="Half-way, the cue says to turn back — for an out-and-back walk."
            onClose={() => setChoosingLength(false)}
          >
            <ChipRow ariaLabel="Planned length">
              {PLAN_CHOICES.map((m) => (
                <Chip
                  key={String(m)}
                  selected={(meta.walkPlanMin ?? null) === m}
                  onClick={() => {
                    setWalkMeta({ walkPlanMin: m });
                    setChoosingLength(false);
                  }}
                >
                  {planWords(m)}
                </Chip>
              ))}
            </ChipRow>
          </Sheet>
        ) : null}
      </IonContent>
    </IonPage>
  );
}
